import { directus } from '@/lib/directus';
import { DirectusService } from './directusService';
import { FallbackService } from './fallbackService';
import { readItems, readItem, createItem, updateItem } from '@directus/sdk';

// Define the Directus types that match your schema
type DirectusFile = {
  id: string;
};

type DirectusProductImage = {
  id: number;
  directus_files_id: string | DirectusFile;
  sort: number;
  products_id?: number;
};

type DirectusProduct = {
  id: number;
  name: string;
  description?: string;
  price: string | number;
  status?: 'draft' | 'published' | 'archived';
  category?: number | { id: number; title: string };
  stock?: number;
  image?: string | { id: string };
  images?: DirectusProductImage[];
  stripe_price_id?: string;
};

export interface ProductImage {
  id: number;
  directus_files_id: {
    id: string;
  };
  sort: number;
}

export interface Product {
  id: number; // Matches Directus integer ID
  name: string;
  description?: string;
  price: number;
  image?: string | { id: string }; // Single image field (UUID or object)
  images?: ProductImage[]; // Multiple images via M2M relationship
  status?: 'draft' | 'published' | 'archived';
  category?: number | { id: number; title: string }; // Foreign key to categories (integer) or category object
  stock?: number; // Available stock count from Directus
  stripe_price_id?: string; // Stripe Price ID for checkout
}

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  parent_category?: string;
  sort_order?: number;
}

export interface ProductFilter {
  category?: string;
  price_min?: number;
  price_max?: number;
  tags?: string[];
  featured?: boolean;
  status?: string;
  search?: string;
}

export class ProductService {
  static getImageUrl(imageId: string, width = 400, height = 400, quality = 80): string {
    if (!imageId) return '';
    return `${DirectusService.getBaseUrl()}/assets/${imageId}?width=${width}&height=${height}&quality=${quality}&fit=cover`;
  }
  static async getProducts(filter?: ProductFilter, limit = 50, offset = 0): Promise<Product[]> {
    try {
      await DirectusService.authenticate();
      
      const queryFilter: Record<string, unknown> = {};
      
      if (filter) {
        if (filter.category) queryFilter.category = { _eq: filter.category };
        if (filter.price_min !== undefined) queryFilter.price = { _gte: filter.price_min };
        if (filter.price_max !== undefined) {
          queryFilter.price = { ...(queryFilter.price as Record<string, unknown> || {}), _lte: filter.price_max };
        }
        if (filter.status) queryFilter.status = { _eq: filter.status };
      }

      // `stripe_price_id` exists in production but may be absent in some
      // environments (e.g. a local Directus). Requesting a non-existent field
      // makes Directus reject the whole query, so try with it first and
      // transparently retry without it if the field isn't available.
      const baseFields = [
        'id', 'name', 'description', 'price', 'status', 'stock', 'image',
        { category: ['id', 'title'] } as any,
        { images: ['id', 'sort', { directus_files_id: ['id'] }] } as any,
      ];
      const runQuery = (fields: unknown[]) =>
        directus.request(
          readItems('products' as const, {
            filter: queryFilter,
            limit,
            offset,
            sort: ['id'],
            fields: fields as any,
          })
        ) as unknown as Promise<DirectusProduct[]>;

      let products: DirectusProduct[];
      try {
        products = await runQuery(['stripe_price_id', ...baseFields]);
      } catch {
        // Field unavailable in this environment — fetch the catalog without it
        // (checkout falls back to inline price_data when stripe_price_id is absent).
        products = await runQuery(baseFields);
      }

      return products as unknown as Product[];
    } catch (error) {
      console.error('Error fetching products:', error);
      // Use fallback data when Directus is down
      console.log('Using fallback product data');
      const fallbackProducts = FallbackService.getProducts();
      
      // Apply filters to fallback data
      let filteredProducts = [...fallbackProducts];
      
      if (filter) {
        if (filter.category) {
          filteredProducts = filteredProducts.filter(p => p.category?.toString() === filter.category);
        }
        if (filter.price_min !== undefined) {
          filteredProducts = filteredProducts.filter(p => p.price >= filter.price_min!);
        }
        if (filter.price_max !== undefined) {
          filteredProducts = filteredProducts.filter(p => p.price <= filter.price_max!);
        }
        if (filter.status) {
          filteredProducts = filteredProducts.filter(p => p.status === filter.status);
        }
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          filteredProducts = filteredProducts.filter(p => 
            p.name.toLowerCase().includes(searchLower) || 
            p.description?.toLowerCase().includes(searchLower)
          );
        }
      }
      
      // Apply pagination
      return filteredProducts.slice(offset, offset + limit) as unknown as Product[];
    }
  }

  static async getProduct(id: number): Promise<Product | null> {
    try {
      await DirectusService.authenticate();
      
      // `stripe_price_id` may be absent in some environments — request it, but
      // retry without it if the field doesn't exist (see getProducts).
      const baseFields = [
        'id', 'name', 'description', 'price', 'status', 'category', 'stock', 'image',
        { images: ['id', 'sort', { directus_files_id: ['id'] }] } as any,
      ];
      const runQuery = (fields: unknown[]) =>
        directus.request(
          readItem('products' as const, id, { fields: fields as any })
        ) as unknown as Promise<DirectusProduct | null>;

      let product: DirectusProduct | null;
      try {
        product = await runQuery(['stripe_price_id', ...baseFields]);
      } catch {
        product = await runQuery(baseFields);
      }

      if (!product) return null;

      // Convert DirectusProduct to Product
      return {
        ...product,
        price: typeof product.price === 'string' ? parseFloat(product.price) : product.price,
        images: (product.images || []).map(img => ({
          id: img.id,
          directus_files_id: {
            id: typeof img.directus_files_id === 'string' 
              ? img.directus_files_id 
              : img.directus_files_id?.id || ''
          },
          sort: img.sort || 0
        }))
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      // Use fallback data when Directus is down
      console.log('Using fallback product data');
      const fallbackProducts = FallbackService.getProducts();
      const product = fallbackProducts.find(p => p.id === id);
      return (product as unknown as Product) || null;
    }
  }

  static async getProductsByCategory(categoryId: number, limit = 20): Promise<Product[]> {
    return this.getProducts({ category: categoryId.toString(), status: 'published' }, limit);
  }

  static async searchProducts(query: string, limit = 20): Promise<Product[]> {
    try {
      await DirectusService.authenticate();
      
      const filter = {
        _or: [
          { name: { _contains: query } },
          { description: { _contains: query } }
        ]
      };
      const baseFields = [
        'id', 'name', 'description', 'price', 'image', 'status', 'category', 'stock',
        'images.directus_files_id.*'
      ];
      const runQuery = (fields: unknown[]) =>
        directus.request(readItems('products', { filter, fields: fields as any, limit }));

      let products;
      try {
        products = await runQuery(['stripe_price_id', ...baseFields]);
      } catch {
        products = await runQuery(baseFields);
      }

      return products as unknown as Product[];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }

  static async createProduct(productData: Omit<Product, 'id'>): Promise<Product | null> {
    try {
      await DirectusService.authenticate();
      
      const { images, ...productWithoutImages } = productData;
      
      const newProduct = await directus.request(
        createItem('products' as const, {
          ...productWithoutImages,
          status: productData.status || 'draft',
          stock: productData.stock || 0
        } as any)
      ) as unknown as DirectusProduct;

      // If there are images to associate, create the relationships
      if (images && images.length > 0) {
        for (const img of images) {
          await directus.request(
            createItem('products_images' as any, {
              products_id: newProduct.id,
              directus_files_id: img.directus_files_id.id,
              sort: img.sort || 0
            })
          );
        }
      }

      return this.getProduct(newProduct.id);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  static async updateProduct(id: number, productData: Partial<Product>): Promise<Product | null> {
    try {
      await DirectusService.authenticate();
      
      const updatedProduct = await directus.request(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateItem('products' as any, id, productData)
      );

      return updatedProduct as unknown as Product;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  static async getCategories(): Promise<ProductCategory[]> {
    try {
      await DirectusService.authenticate();
      
      const categories = await directus.request(
        readItems('categories', {
          sort: ['title', 'id'],
          fields: ['*']
        })
      );

      return categories.map((category: Record<string, unknown>) => ({
        id: category.id as string,
        name: (category.title as string) || '',
        title: category.title as string,
        slug: category.slug as string,
        image: category.image as string
      })) as ProductCategory[];
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Use fallback data when Directus is down
      console.log('Using fallback category data');
      const fallbackCategories = FallbackService.getCategories();
      return fallbackCategories.map(category => ({
        id: category.id as string,
        name: category.title || '',
        title: category.title || '',
        slug: category.slug as string,
        image: category.image as string
      })) as ProductCategory[];
    }
  }

  static async getCategory(slug: string): Promise<ProductCategory | null> {
    try {
      await DirectusService.authenticate();
      
      const categories = await directus.request(
        readItems('categories', {
          filter: { slug: { _eq: slug } }
        })
      );

      const category = categories[0];
      if (!category) return null;
      
      return {
        id: category.id as string,
        name: (category.title as string) || '',
        title: category.title as string,
        slug: category.slug as string,
        image: category.image as string
      } as ProductCategory;
    } catch (error) {
      console.error('Error fetching category:', error);
      // Use fallback data when Directus is down
      console.log('Using fallback category data');
      const fallbackCategory = FallbackService.getCategory(slug);
      if (!fallbackCategory) return null;
      
      return {
        id: fallbackCategory.id as string,
        name: fallbackCategory.title || '',
        title: fallbackCategory.title || '',
        slug: fallbackCategory.slug as string,
        image: fallbackCategory.image as string
      } as ProductCategory;
    }
  }

  /**
   * Get all product images (handles both single image and M2M images)
   * @param product - Product object
   * @returns Array of image UUIDs
   */
  static getProductImages(product: Product): string[] {
    if (!product.images || product.images.length === 0) return [];
    
    return product.images
      .sort((a, b) => (a.sort || 0) - (b.sort || 0))
      .map(img => 
        typeof img.directus_files_id === 'string' 
          ? img.directus_files_id 
          : img.directus_files_id?.id || ''
      )
      .filter(Boolean);
  }

  /**
   * Get primary product image (first image)
   * @param product - Product object
   * @returns Image UUID or null
   */
  static getPrimaryImage(product: Product): string | null {
    const images = this.getProductImages(product);
    return images[0] || null;
  }

  /**
   * Get all image URLs for display
   * @param product - Product object
   * @returns Array of full image URLs
   */
  static getProductImageUrls(product: Product): string[] {
    return this.getProductImages(product).map(imageId => 
      DirectusService.getImageUrl(imageId)
    );
  }
}
